import { useEffect, useRef, useState } from "react";
import { api, handleSessionExpired, UnauthorizedError } from "@/api/client";

// 경고 다이얼로그의 "로그아웃" — 서버 세션 파기 후 "/" 이동.
// ⚠ loginUrl("/")로 보내면 안 된다: /login은 NW SSO Authorize로 리다이렉트하는데,
//   IdP 세션은 로그아웃해도 살아있으므로 즉시 재인가되어 "로그아웃이 안 되는" 버그가 된다.
//   me()가 public이므로 "/" 이동 시 Gate(App.tsx)가 로그인 화면을 렌더한다
//   (Chat.tsx onLogout과 동일 동작, 2026-07-03 로그아웃 수정과 정합).
const doLogout = async () => {
  try {
    await api.logout();
  } catch {
    /* 이미 만료 등 — 무시하고 로그인 화면으로 */
  } finally {
    window.location.href = "/";
  }
};

const IDLE_LIMIT = 30 * 60 * 1000; // 세션 idle 만료(서버 30분)
const WARN_AT = 27 * 60 * 1000; // 만료 3분 전 경고
const RENEW_EVERY = 10 * 60 * 1000; // 활성 사용자 자동 갱신 주기

/** 세션 만료 사전 경고 + 활성 시 자동 갱신. */
export default function SessionGuard() {
  const [warn, setWarn] = useState(false);
  const lastActivity = useRef(Date.now());
  const lastRenew = useRef(Date.now());

  useEffect(() => {
    const onActivity = () => {
      lastActivity.current = Date.now();
    };
    // 읽기(스크롤/휠/터치)도 활동으로 인정 — 답변을 읽는 동안 idle 로그아웃되지 않도록.
    // ⚠ visibilitychange 는 여기서 제외한다. 탭을 떠났다 돌아오는 것만으로 idle 타이머가
    //   리셋되면, 서버 세션은 이미 만료됐는데 클라이언트는 "활성"으로 오인해 화면을 계속
    //   띄운다(= 화면은 보이는데 기능만 깨지는 문제). 복귀 시점엔 리셋 대신 세션을 검증한다.
    const events = ["pointerdown", "keydown", "wheel", "scroll", "touchstart"];
    events.forEach((e) =>
      window.addEventListener(e, onActivity, { passive: true, capture: true }),
    );

    // 서버 세션 검증 → 만료면 즉시 자동 로그오프. 살아있으면 슬라이드 타이밍 갱신.
    const verifySession = async () => {
      try {
        await api.me();
        lastRenew.current = Date.now();
      } catch (e) {
        if (e instanceof UnauthorizedError) handleSessionExpired(); // 만료 → 로그인 전환
        /* 그 외(네트워크 등)는 다음 주기/요청에서 처리 */
      }
    };

    // 탭 복귀 시: idle 타이머를 리셋하지 않고 서버 세션이 살아있는지 즉시 확인한다.
    const onVisible = () => {
      if (document.visibilityState === "visible") verifySession();
    };
    window.addEventListener("visibilitychange", onVisible);

    const iv = setInterval(() => {
      const idle = Date.now() - lastActivity.current;
      if (idle >= IDLE_LIMIT) {
        // ⚠ 과거: 서버 상태 확인 없이 handleSessionExpired()를 즉시 호출했다.
        //   그런데 공지 폴링(AnnouncementBanner, 10분 주기) 등 보호 API 호출이 서버 세션을
        //   계속 슬라이드하므로, 서버는 유효한데 클라이언트 타이머만 만료된 상태로 리다이렉트
        //   → NW IdP가 조용히 재인가 → 사용자에겐 "저절로 새로고침"으로 보이는 버그가 됐다
        //   (api.logout()도 호출하지 않아 실제 로그오프 효과도 없었다).
        // 이제 서버 세션을 검증해 살아있으면 유지하고, 실제 만료(401)일 때만 로그오프한다.
        // 숨김 탭에서는 아무것도 하지 않는다 — 복귀 시 onVisible 이 검증한다(백그라운드
        // 리다이렉트로 몰래 새로고침되는 것 방지). 검증은 RENEW_EVERY 주기로 스로틀.
        if (
          document.visibilityState === "visible" &&
          Date.now() - lastRenew.current >= RENEW_EVERY
        ) {
          verifySession();
        }
        return;
      }
      if (idle >= WARN_AT) {
        setWarn(true);
        return;
      }
      setWarn(false);
      // 활성 사용자(최근 5분 내 활동, 화면 보임) → 주기적으로 세션 슬라이드 겸 검증
      if (
        document.visibilityState === "visible" &&
        idle < 5 * 60 * 1000 &&
        Date.now() - lastRenew.current >= RENEW_EVERY
      ) {
        verifySession();
      }
    }, 30 * 1000);

    return () => {
      events.forEach((e) =>
        window.removeEventListener(e, onActivity, { capture: true }),
      );
      window.removeEventListener("visibilitychange", onVisible);
      clearInterval(iv);
    };
  }, []);

  const extend = async () => {
    try {
      await api.me();
    } catch (e) {
      // me()가 public으로 바뀌어 401 자동 로그오프가 없어졌으므로 여기서 직접 처리.
      if (e instanceof UnauthorizedError) {
        handleSessionExpired(); // 이미 만료 → 로그인 전환
        return;
      }
      /* 네트워크 오류 등은 무시 — 다음 주기에서 재검증 */
    }
    lastActivity.current = Date.now();
    lastRenew.current = Date.now();
    setWarn(false);
  };

  if (!warn) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-xs rounded-xl bg-card p-4 text-center shadow-xl">
        <p className="text-sm font-medium">세션이 곧 만료됩니다</p>
        <p className="mt-1 text-xs text-muted-foreground">계속 이용하시려면 연장해 주세요.</p>
        <div className="mt-3 flex gap-2">
          <button
            onClick={extend}
            className="flex-1 rounded-lg bg-[#EB4E26] py-2 text-sm font-semibold text-white"
          >
            계속하기
          </button>
          <button
            onClick={doLogout}
            className="rounded-lg border border-border px-3 py-2 text-sm"
          >
            로그아웃
          </button>
        </div>
      </div>
    </div>
  );
}
